import type { RequiredPermissions } from 'types/Permissions';

import {
  INVITE_APPRAISERS_PERMISSIONS,
  SUBMIT_APPRAISAL_PERMISSIONS,
} from 'components/sections/createModify/inventoryItems/tradeInItem/tradeInAppraisalPermissions';
import { BuilderType } from 'enums/builderType';

import { AccessLevel, ResourceType } from 'store/client/graphql';

/**
 * Kept outside the builder chunk so permission gates can run before builder code downloads.
 */
export const builderPermissions: Readonly<Record<BuilderType, RequiredPermissions>> = {
  [BuilderType.APPOINTMENT_CREATE]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.APPOINTMENTS }],
  [BuilderType.APPOINTMENT_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.APPOINTMENTS }],
  [BuilderType.CONTACT_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.CONTACTS }],
  [BuilderType.CONTACT_MODIFY]: [{ level: AccessLevel.FULL, resource: ResourceType.CONTACTS }],
  [BuilderType.CONVERSATION_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.LEADS }],
  [BuilderType.DIGITAL_RETAILING_TOOL_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }],
  [BuilderType.GROUP_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.GROUPS }],
  [BuilderType.GROUP_MODIFY]: [{ level: AccessLevel.FULL, resource: ResourceType.GROUPS }],
  [BuilderType.GROUP_PRICING_CASH_SEGMENT_CREATE]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }],
  [BuilderType.GROUP_PRICING_CASH_SEGMENT_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }],
  [BuilderType.GROUP_PRICING_FINANCE_SEGMENT_CREATE]: [
    {
      level: AccessLevel.ADVANCED,
      resource: ResourceType.ROOFTOPS,
    },
  ],
  [BuilderType.GROUP_PRICING_FINANCE_SEGMENT_MODIFY]: [
    {
      level: AccessLevel.ADVANCED,
      resource: ResourceType.ROOFTOPS,
    },
  ],
  [BuilderType.GROUP_PRICING_LEASE_SEGMENT_AVAILABLE_MILEAGE_ALLOWANCE_CREATE]: [
    {
      level: AccessLevel.ADVANCED,
      resource: ResourceType.ROOFTOPS,
    },
  ],
  [BuilderType.GROUP_PRICING_LEASE_SEGMENT_CREATE]: [
    {
      level: AccessLevel.ADVANCED,
      resource: ResourceType.ROOFTOPS,
    },
  ],
  [BuilderType.GROUP_PRICING_LEASE_SEGMENT_MODIFY]: [
    {
      level: AccessLevel.ADVANCED,
      resource: ResourceType.ROOFTOPS,
    },
  ],
  [BuilderType.LEAD_ACTIVITY_CREATE]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.LEADS }],
  [BuilderType.LEAD_ACTIVITY_CREATE_LINKED_RETAIL_ITEMS]: [
    { level: AccessLevel.ADVANCED, resource: ResourceType.LEADS },
  ],
  [BuilderType.LEAD_ACTIVITY_CREATE_REPLY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.LEADS }],
  /*
   * Normally, ADVANCED permissions are needed to create/modify entities, but users with BASIC permission
   * are able to edit lead activities assigned to them
   */
  [BuilderType.LEAD_ACTIVITY_MODIFY]: [{ level: AccessLevel.BASIC, resource: ResourceType.LEADS }],
  [BuilderType.LEAD_ACTIVITY_NOTE]: [{ level: AccessLevel.BASIC, resource: ResourceType.LEADS }],
  [BuilderType.LEAD_CREATE]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.LEADS }],
  [BuilderType.LEAD_MODIFY]: [{ level: AccessLevel.BASIC, resource: ResourceType.LEADS }],
  [BuilderType.MAILBOX_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.MAILBOXES }],
  [BuilderType.MAILBOX_MODIFY]: [{ level: AccessLevel.FULL, resource: ResourceType.MAILBOXES }],
  [BuilderType.MAKE_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.MAKES_MODELS_SUB_MODELS_TRIMS }],
  [BuilderType.MODEL_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.MAKES_MODELS_SUB_MODELS_TRIMS }],
  [BuilderType.PURCHASE_ACTIVITY_CREATE]: [
    { level: AccessLevel.ADVANCED, resource: ResourceType.RETAIL_ITEMS },
    { level: AccessLevel.ADVANCED, resource: ResourceType.LEADS },
  ],
  [BuilderType.PURCHASE_ACTIVITY_MODIFY]: [
    { level: AccessLevel.ADVANCED, resource: ResourceType.RETAIL_ITEMS },
    { level: AccessLevel.ADVANCED, resource: ResourceType.LEADS },
  ],
  [BuilderType.RETAIL_BULK_ADJUSTMENT_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.RETAIL_ITEMS }],
  [BuilderType.RETAIL_BULK_ADJUSTMENT_MODIFY]: [{ level: AccessLevel.FULL, resource: ResourceType.RETAIL_ITEMS }],
  [BuilderType.RETAIL_FILTER]: [{ level: AccessLevel.BASIC, resource: ResourceType.RETAIL_ITEMS }],
  [BuilderType.RETAIL_ITEM_PRICE_ADJUSTMENT_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.RETAIL_ITEMS }],
  [BuilderType.RETAIL_ITEM_PRICE_ADJUSTMENT_MODIFY]: [{ level: AccessLevel.FULL, resource: ResourceType.RETAIL_ITEMS }],
  [BuilderType.RETAIL_ITEM_PRICING]: [{ level: AccessLevel.FULL, resource: ResourceType.RETAIL_ITEMS }],
  [BuilderType.RETAIL_ITEM_PRICING_INFO]: [{ level: AccessLevel.FULL, resource: ResourceType.RETAIL_ITEMS }],
  [BuilderType.RETAIL_ITEMS_CREATE]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.RETAIL_ITEMS }],
  [BuilderType.RETAIL_ITEMS_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.RETAIL_ITEMS }],
  [BuilderType.RETAIL_PORTAL_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }],
  [BuilderType.ROOFTOP_DEPARTMENTS_CREATE]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }],
  [BuilderType.ROOFTOP_DEPARTMENTS_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }],
  [BuilderType.ROOFTOP_INTEGRATION_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS }],
  [BuilderType.ROOFTOP_INTEGRATION_EBLOCK_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS }],
  [BuilderType.ROOFTOP_INTEGRATION_EBLOCK_MODIFY]: [{ level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS }],
  [BuilderType.ROOFTOP_INTEGRATION_INVENTORY_EXPORT_CREATE]: [
    { level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS },
  ],
  [BuilderType.ROOFTOP_INTEGRATION_INVENTORY_EXPORT_MODIFY]: [
    { level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS },
  ],
  [BuilderType.ROOFTOP_INTEGRATION_INVENTORY_IMPORT_CREATE]: [
    { level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS },
  ],
  [BuilderType.ROOFTOP_INTEGRATION_INVENTORY_IMPORT_MODIFY]: [
    { level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS },
  ],
  [BuilderType.ROOFTOP_INTEGRATION_MODIFY]: [{ level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS }],
  [BuilderType.ROOFTOP_INTEGRATION_OEM_ACCOUNT_EXPORT_CREATE]: [
    { level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS },
  ],
  [BuilderType.ROOFTOP_INTEGRATION_OEM_ACCOUNT_EXPORT_MODIFY]: [
    { level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS },
  ],
  [BuilderType.ROOFTOP_INTEGRATION_PHONE_CALL_TRACKING_CREATE]: [
    { level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS },
  ],
  [BuilderType.ROOFTOP_INTEGRATION_PHONE_CALL_TRACKING_MODIFY]: [
    { level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS },
  ],
  [BuilderType.ROOFTOP_INTEGRATION_PRICING_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS }],
  [BuilderType.ROOFTOP_INTEGRATION_PRICING_MODIFY]: [{ level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS }],
  [BuilderType.ROOFTOP_LEAD_FORWARD_CREATE]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }],
  [BuilderType.ROOFTOP_LEAD_FORWARD_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }],
  [BuilderType.ROOFTOPS_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.ROOFTOPS }],
  [BuilderType.ROOFTOPS_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }],
  [BuilderType.SUBMODEL_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.MAKES_MODELS_SUB_MODELS_TRIMS }],
  [BuilderType.TAGS_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.TAGS }],
  [BuilderType.TAGS_MODIFY]: [{ level: AccessLevel.FULL, resource: ResourceType.TAGS }],
  [BuilderType.TASK_CREATE]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.TASKS }],
  [BuilderType.TASK_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.TASKS }],
  [BuilderType.TRADE_IN_ITEMS_CREATE]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.TRADE_IN_ITEMS }],
  [BuilderType.TRADE_IN_ITEMS_GUARANTEED_TRADE]: [
    { level: AccessLevel.ADVANCED, resource: ResourceType.TRADE_IN_ITEMS },
  ],
  [BuilderType.TRADE_IN_ITEMS_INVITE_APPRAISERS]: INVITE_APPRAISERS_PERMISSIONS,
  [BuilderType.TRADE_IN_ITEMS_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.TRADE_IN_ITEMS }],
  [BuilderType.TRADE_IN_ITEMS_SUBMIT_APPRAISAL]: SUBMIT_APPRAISAL_PERMISSIONS,
  [BuilderType.TRADE_IN_TOOL_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }],
  [BuilderType.TRADE_IN_VALUATION_ADJUSTMENT_CREATE]: [
    { level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS },
  ],
  [BuilderType.TRADE_IN_VALUATION_ADJUSTMENT_MODIFY]: [
    { level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS },
  ],
  [BuilderType.TRIM_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.MAKES_MODELS_SUB_MODELS_TRIMS }],
  [BuilderType.TRIM_PHOTOS_FILTER]: [{ level: AccessLevel.FULL, resource: ResourceType.MAKES_MODELS_SUB_MODELS_TRIMS }],
  [BuilderType.USER_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.USERS }],
  [BuilderType.USER_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.USERS }],
  [BuilderType.USER_PASSWORD_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.USERS }],
  [BuilderType.WEBSITE_CREATE]: [{ level: AccessLevel.FULL, resource: ResourceType.WEBSITES }],
  [BuilderType.WEBSITE_LEAD_ROUTING_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.WEBSITES }],
  [BuilderType.WEBSITE_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.WEBSITES }],
  [BuilderType.WEBSITE_ROUTE_CREATE]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.WEBSITES }],
  [BuilderType.WEBSITE_ROUTE_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.WEBSITES }],
  [BuilderType.WEBSITE_ROUTE_RETAIL_FILTER]: [{ level: AccessLevel.BASIC, resource: ResourceType.RETAIL_ITEMS }],
  [BuilderType.WEBSITE_SHOPPING_TOOLS_MODIFY]: [{ level: AccessLevel.ADVANCED, resource: ResourceType.WEBSITES }],
};
